'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';

import { useSession, signOut } from 'next-auth/react';
const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [logo, setLogo] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const { data: session } = useSession();

  useEffect(() => {
    async function fetchLogo() {
      try {
        const response = await fetch(`${STRAPI_URL}/api/page-accueil?populate=*`, {
          cache: 'no-store',
        });
        const data = await response.json();
        const logoUrl = data?.data?.logo?.url;
        if (logoUrl) setLogo(`${STRAPI_URL}${logoUrl}`);
      } catch (error) {
        console.error('Erreur chargement logo:', error);
      }
    }
    fetchLogo();

    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = async () => {
    await signOut({ redirect: false });
    window.location.href = '/';
  };

  return (
    <header
      className={`bg-[#006C8F] sticky top-0 z-50 transition-shadow duration-300 ${
        scrolled ? 'shadow-xl shadow-[#006C8F]/30' : ''
      }`}
    >
      {/* Fine ligne signature jaune */}
      <div className="h-[3px] bg-[#FFC72C]" />

      <nav className="container mx-auto px-6 py-3.5">
        <div className="flex items-center justify-between">
          {/* Logo + identité */}
          <Link href="/" className="flex items-center gap-3 group">
            {logo ? (
              <div className="w-11 h-11 bg-white rounded-full flex items-center justify-center p-1.5 shadow-md group-hover:shadow-lg transition-shadow flex-shrink-0">
                {/* eslint-disable-next-line @next/next/no-img-element -- images Strapi (hôte variable selon l’environnement) dans des conteneurs dimensionnés en CSS : le passage à next/image demande une vérification visuelle et l’ajout de l’hôte aux remotePatterns. */}
                <img src={logo} alt="SCH Logo" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-11 h-11 bg-[#FFC72C] rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow flex-shrink-0">
                <span className="text-[#006C8F] font-bold text-xs">SCH</span>
              </div>
            )}
            <div className="leading-none">
              <span
                className="block text-white font-bold text-base leading-tight"
                style={{ fontFamily: 'var(--font-playfair), serif' }}
              >
                Sporting Club Houlgate
              </span>
              <span className="block text-[#FFC72C]/80 text-[10px] font-medium tracking-[0.18em] uppercase mt-0.5">
                Depuis 1906
              </span>
            </div>
          </Link>

          {/* Navigation desktop */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              href="/"
              className="nav-link text-white/85 hover:text-white font-medium text-sm tracking-wide transition-colors"
            >
              Accueil
            </Link>
            <Link
              href="/actualites"
              className="nav-link text-white/85 hover:text-white font-medium text-sm tracking-wide transition-colors"
            >
              Actualités
            </Link>
            <Link
              href="/stages"
              className="nav-link text-white/85 hover:text-white font-medium text-sm tracking-wide transition-colors"
            >
              Stages
            </Link>
            <Link
              href="/interclubs"
              className="nav-link text-white/85 hover:text-white font-medium text-sm tracking-wide transition-colors"
            >
              Interclubs
            </Link>
            <Link
              href="/calendrier"
              className="nav-link text-white/85 hover:text-white font-medium text-sm tracking-wide transition-colors"
            >
              Calendrier
            </Link>
            <Link
              href="/histoire"
              className="nav-link text-white/85 hover:text-white font-medium text-sm tracking-wide transition-colors"
            >
              Histoire
            </Link>
            <Link
              href="/contact"
              className="nav-link text-white/85 hover:text-white font-medium text-sm tracking-wide transition-colors"
            >
              Contact
            </Link>
            {session ? (
              <button
                onClick={handleLogout}
                className="px-5 py-2 bg-white/15 text-white font-bold text-sm rounded-full hover:bg-white/25 transition-colors border border-white/20"
              >
                Déconnexion
              </button>
            ) : (
              <Link
                href="/login"
                className="px-5 py-2 bg-[#FFC72C] text-[#006C8F] font-bold text-sm rounded-full hover:bg-white transition-colors shadow-sm"
              >
                Connexion
              </Link>
            )}
          </div>

          {/* Hamburger mobile */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Menu mobile */}
        {isOpen && (
          <div className="md:hidden mt-3 pb-3 border-t border-white/15 pt-3 space-y-1">
            <Link
              href="/"
              onClick={() => setIsOpen(false)}
              className="block py-2.5 px-3 text-white/85 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-medium text-sm"
            >
              Accueil
            </Link>
            <Link
              href="/actualites"
              onClick={() => setIsOpen(false)}
              className="block py-2.5 px-3 text-white/85 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-medium text-sm"
            >
              Actualités
            </Link>
            <Link
              href="/stages"
              onClick={() => setIsOpen(false)}
              className="block py-2.5 px-3 text-white/85 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-medium text-sm"
            >
              Stages
            </Link>
            <Link
              href="/interclubs"
              onClick={() => setIsOpen(false)}
              className="block py-2.5 px-3 text-white/85 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-medium text-sm"
            >
              Interclubs
            </Link>
            <Link
              href="/calendrier"
              onClick={() => setIsOpen(false)}
              className="block py-2.5 px-3 text-white/85 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-medium text-sm"
            >
              Calendrier
            </Link>
            <Link
              href="/histoire"
              onClick={() => setIsOpen(false)}
              className="block py-2.5 px-3 text-white/85 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-medium text-sm"
            >
              Histoire
            </Link>
            <Link
              href="/contact"
              onClick={() => setIsOpen(false)}
              className="block py-2.5 px-3 text-white/85 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-medium text-sm"
            >
              Contact
            </Link>
            {session ? (
              <button
                onClick={handleLogout}
                className="block w-full text-left mt-2 py-2.5 px-3 text-white/85 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-medium text-sm"
              >
                Déconnexion
              </button>
            ) : (
              <Link
                href="/login"
                onClick={() => setIsOpen(false)}
                className="block mt-2 py-2.5 px-3 bg-[#FFC72C] text-[#006C8F] rounded-lg font-bold text-center text-sm"
              >
                Connexion
              </Link>
            )}
          </div>
        )}
      </nav>
    </header>
  );
}
